import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiDashboardAPI, notificationsAPI, casesAPI } from '../lib/api';
import { formatDate, formatRelativeTime, getStatusColor } from '../lib/utils';
import { 
  Brain, 
  AlertTriangle, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  RefreshCw,
  ChevronRight,
  Briefcase,
  TrendingUp,
  TrendingDown,
  Activity,
  Bell,
  X,
  Sparkles,
  FileText,
  Target,
  Shield,
  Eye,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PageLoader } from '../components/common/LoadingSpinner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const getHealthColor = (status) => {
  switch (status) {
    case 'healthy': return 'text-green-600 bg-green-100';
    case 'attention_needed': return 'text-yellow-600 bg-yellow-100';
    case 'at_risk': return 'text-orange-600 bg-orange-100';
    case 'critical': return 'text-red-600 bg-red-100';
    default: return 'text-slate-600 bg-slate-100';
  }
};

const getHealthIcon = (status) => {
  switch (status) {
    case 'healthy': return <CheckCircle className="h-5 w-5 text-green-600" />;
    case 'attention_needed': return <AlertCircle className="h-5 w-5 text-yellow-600" />;
    case 'at_risk': return <AlertTriangle className="h-5 w-5 text-orange-600" />;
    case 'critical': return <AlertTriangle className="h-5 w-5 text-red-600" />;
    default: return <Activity className="h-5 w-5 text-slate-600" />;
  }
};

const getSeverityColor = (severity) => {
  switch (severity) {
    case 'critical': return 'bg-red-100 text-red-800 border-red-200';
    case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'info': return 'bg-blue-100 text-blue-800 border-blue-200';
    default: return 'bg-slate-100 text-slate-800 border-slate-200';
  }
};

export const CaseHealthDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseDetails, setCaseDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState('summary');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setRefreshing(true);
      const response = await aiDashboardAPI.getDashboard();
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load Case Health Dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleViewCase = async (caseItem) => {
    setSelectedCase(caseItem);
    setLoadingDetails(true);
    setCaseDetails(null);
    setActiveDetailTab('summary');
    
    try {
      const [summaryRes, risksRes] = await Promise.all([
        casesAPI.getAISummary(caseItem.case_id),
        casesAPI.getAIRisks(caseItem.case_id)
      ]);
      
      setCaseDetails({
        summary: summaryRes.data,
        risks: risksRes.data
      });
    } catch (error) {
      console.error('Failed to load case details:', error);
      toast.error('Failed to load AI analysis');
    } finally {
      setLoadingDetails(false);
    }
  };

  const loadNextSteps = async () => {
    if (!selectedCase) return;
    setLoadingDetails(true);
    try {
      const response = await casesAPI.getAINextSteps(selectedCase.case_id);
      setCaseDetails(prev => ({ ...prev, nextSteps: response.data }));
    } catch (error) {
      toast.error('Failed to load next steps');
    } finally {
      setLoadingDetails(false);
    }
  };

  const loadInsights = async () => {
    if (!selectedCase) return;
    setLoadingDetails(true);
    try {
      const response = await casesAPI.getAIInsights(selectedCase.case_id);
      setCaseDetails(prev => ({ ...prev, insights: response.data }));
    } catch (error) {
      toast.error('Failed to load insights');
    } finally {
      setLoadingDetails(false);
    }
  };

  const dismissNotification = async (notificationId) => {
    try {
      await notificationsAPI.dismiss(notificationId);
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to dismiss notification');
    }
  };

  if (loading) {
    return <PageLoader text="Loading AI Case Intelligence..." />;
  }

  if (!dashboard) {
    return (
      <div className="text-center py-12">
        <Brain className="h-12 w-12 mx-auto text-slate-300 mb-4" />
        <p className="text-slate-500">Failed to load dashboard</p>
        <Button variant="link" onClick={fetchDashboard}>Try again</Button>
      </div>
    );
  }

  const { firm_health, risk_summary, cases, critical_alerts, notifications } = dashboard;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="case-health-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <Brain className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">AI Case Intelligence</h1>
            <p className="text-slate-500">Daily command center for case awareness</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchDashboard} 
          disabled={refreshing}
          className="gap-2"
          data-testid="refresh-dashboard-btn"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh Analysis
        </Button>
      </div>

      {/* Firm Health Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="col-span-1 md:col-span-2 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-300 text-sm">Firm Case Health</p>
                <p className="text-4xl font-bold mt-2">{firm_health.average_score}%</p>
                <Badge className={`mt-2 ${getHealthColor(firm_health.status)}`}>
                  {firm_health.status.replace('_', ' ').toUpperCase()}
                </Badge>
              </div>
              <div className="text-right">
                <div className="h-24 w-24 relative">
                  <svg className="h-24 w-24 transform -rotate-90">
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      fill="none"
                      stroke="rgba(255,255,255,0.2)"
                      strokeWidth="8"
                    />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      fill="none"
                      stroke={firm_health.average_score >= 80 ? '#22c55e' : firm_health.average_score >= 60 ? '#eab308' : '#ef4444'}
                      strokeWidth="8"
                      strokeDasharray={`${(firm_health.average_score / 100) * 251.2} 251.2`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    {getHealthIcon(firm_health.status)}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-2 gap-4">
              <div>
                <p className="text-slate-400 text-xs">Active Cases</p>
                <p className="text-xl font-bold">{firm_health.total_cases}</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs">Cases at Risk</p>
                <p className="text-xl font-bold text-orange-400">{firm_health.cases_at_risk}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <p className="text-sm font-medium text-slate-700">Critical Risks</p>
            </div>
            <p className="text-3xl font-bold text-red-600">{risk_summary.critical}</p>
            <p className="text-xs text-slate-500 mt-1">Require immediate attention</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              <p className="text-sm font-medium text-slate-700">Warnings</p>
            </div>
            <p className="text-3xl font-bold text-yellow-600">{risk_summary.warning}</p>
            <p className="text-xs text-slate-500 mt-1">Need review soon</p>
          </CardContent>
        </Card>
      </div>

      {/* Critical Alerts */}
      {critical_alerts.length > 0 && (
        <Card className="border-red-200 bg-red-50" data-testid="critical-alerts">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-red-800">
              <AlertTriangle className="h-5 w-5" />
              Critical Alerts - Immediate Action Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {critical_alerts.map((alert) => (
                <div 
                  key={alert.case_id}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-200 cursor-pointer hover:border-red-300 transition-colors"
                  onClick={() => handleViewCase(alert)}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{alert.title}</p>
                      <p className="text-sm text-slate-500">{alert.client_name} • {alert.top_risk?.title}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className="bg-red-100 text-red-800">{alert.critical_risks} critical</Badge>
                    <ChevronRight className="h-5 w-5 text-slate-400" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cases List */}
        <div className="lg:col-span-2">
          <Card data-testid="cases-health-list">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-blue-600" />
                Case Health Monitor
              </CardTitle>
              <CardDescription>Cases sorted by health score (lowest first)</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {cases.map((caseItem) => (
                    <div
                      key={caseItem.case_id}
                      className="p-4 rounded-lg border border-slate-200 hover:border-slate-300 cursor-pointer transition-all hover:shadow-sm"
                      onClick={() => handleViewCase(caseItem)}
                      data-testid={`case-item-${caseItem.case_id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${getHealthColor(caseItem.health_status).split(' ')[1]}`}>
                            {getHealthIcon(caseItem.health_status)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-slate-900">{caseItem.title}</h4>
                              {caseItem.critical_risks > 0 && (
                                <Badge className="bg-red-100 text-red-800 text-xs">
                                  {caseItem.critical_risks} critical
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500">{caseItem.client_name} • {caseItem.case_number || 'Pending'}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">{caseItem.practice_area}</Badge>
                              {caseItem.top_risk && (
                                <span className="text-xs text-slate-400">
                                  Top risk: {caseItem.top_risk.title}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <div className="w-16">
                              <Progress value={caseItem.health_score} className="h-2" />
                            </div>
                            <span className={`text-sm font-bold ${
                              caseItem.health_score >= 80 ? 'text-green-600' :
                              caseItem.health_score >= 60 ? 'text-yellow-600' :
                              caseItem.health_score >= 40 ? 'text-orange-600' : 'text-red-600'
                            }`}>
                              {caseItem.health_score}%
                            </span>
                          </div>
                          {caseItem.last_activity && (
                            <p className="text-xs text-slate-400 mt-1">
                              Last activity: {formatRelativeTime(caseItem.last_activity)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Notifications Panel */}
        <div>
          <Card data-testid="notifications-panel">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell className="h-5 w-5 text-orange-600" />
                Risk Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {notifications.length > 0 ? (
                  <div className="space-y-3">
                    {notifications.map((notif) => (
                      <div 
                        key={notif.notification_id}
                        className={`p-3 rounded-lg border ${getSeverityColor(notif.severity)}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{notif.title}</p>
                            <p className="text-xs mt-1 opacity-80">{notif.description}</p>
                            <p className="text-xs mt-2 opacity-60">
                              {formatRelativeTime(notif.created_at)} • {notif.source}
                            </p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0 text-slate-400 hover:text-slate-600 shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              dismissNotification(notif.notification_id);
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-2" />
                    <p>No active alerts</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Case Detail Dialog */}
      <Dialog open={!!selectedCase} onOpenChange={() => setSelectedCase(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-600" />
              AI Case Analysis: {selectedCase?.title}
            </DialogTitle>
            <DialogDescription>
              {selectedCase?.client_name} • {selectedCase?.case_number || 'Pending'}
            </DialogDescription>
          </DialogHeader>

          {loadingDetails && !caseDetails ? (
            <div className="py-12 text-center">
              <RefreshCw className="h-8 w-8 mx-auto animate-spin text-purple-600" />
              <p className="mt-4 text-slate-500">Analyzing case...</p>
            </div>
          ) : caseDetails && (
            <Tabs value={activeDetailTab} onValueChange={setActiveDetailTab} className="mt-4">
              <TabsList className="grid grid-cols-4">
                <TabsTrigger value="summary" className="gap-1">
                  <FileText className="h-4 w-4" />
                  Summary
                </TabsTrigger>
                <TabsTrigger value="risks" className="gap-1">
                  <Shield className="h-4 w-4" />
                  Risks
                </TabsTrigger>
                <TabsTrigger value="next" className="gap-1" onClick={loadNextSteps}>
                  <Target className="h-4 w-4" />
                  Next Steps
                </TabsTrigger>
                <TabsTrigger value="insights" className="gap-1" onClick={loadInsights}>
                  <Zap className="h-4 w-4" />
                  Insights
                </TabsTrigger>
              </TabsList>

              <TabsContent value="summary" className="mt-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="prose prose-sm max-w-none">
                      <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 bg-slate-50 p-4 rounded-lg">
                        {caseDetails.summary?.summary}
                      </pre>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                      <span>Generated: {formatDate(caseDetails.summary?.generated_at)}</span>
                      <span className="italic">{caseDetails.summary?.disclaimer}</span>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="risks" className="mt-4">
                <div className="space-y-4">
                  {/* Health Score */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">Case Health Score</p>
                          <p className="text-3xl font-bold">{caseDetails.risks?.health_score?.score}%</p>
                          <Badge className={getHealthColor(caseDetails.risks?.health_score?.status)}>
                            {caseDetails.risks?.health_score?.status?.replace('_', ' ').toUpperCase()}
                          </Badge>
                        </div>
                        <div className="h-16 w-16 relative">
                          <svg className="h-16 w-16 transform -rotate-90">
                            <circle cx="32" cy="32" r="28" fill="none" stroke="#e2e8f0" strokeWidth="6" />
                            <circle
                              cx="32" cy="32" r="28" fill="none"
                              stroke={caseDetails.risks?.health_score?.score >= 80 ? '#22c55e' : 
                                     caseDetails.risks?.health_score?.score >= 60 ? '#eab308' : '#ef4444'}
                              strokeWidth="6"
                              strokeDasharray={`${(caseDetails.risks?.health_score?.score / 100) * 175.9} 175.9`}
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Risks List */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Identified Risks ({caseDetails.risks?.risks?.length || 0})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {caseDetails.risks?.risks?.length > 0 ? (
                        <div className="space-y-2">
                          {caseDetails.risks.risks.map((risk, idx) => (
                            <div key={idx} className={`p-3 rounded-lg border ${getSeverityColor(risk.severity)}`}>
                              <div className="flex items-start gap-2">
                                {risk.severity === 'critical' ? <AlertTriangle className="h-4 w-4 mt-0.5" /> :
                                 risk.severity === 'warning' ? <AlertCircle className="h-4 w-4 mt-0.5" /> :
                                 <Activity className="h-4 w-4 mt-0.5" />}
                                <div>
                                  <p className="font-medium text-sm">{risk.title}</p>
                                  <p className="text-xs mt-1 opacity-80">{risk.description}</p>
                                  <p className="text-xs mt-1 opacity-60">Source: {risk.source}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center py-4 text-slate-500">No risks detected</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* AI Analysis */}
                  {caseDetails.risks?.ai_analysis && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-purple-600" />
                          AI Risk Analysis
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 bg-slate-50 p-4 rounded-lg">
                          {caseDetails.risks.ai_analysis}
                        </pre>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="next" className="mt-4">
                {loadingDetails ? (
                  <div className="py-8 text-center">
                    <RefreshCw className="h-6 w-6 mx-auto animate-spin text-purple-600" />
                  </div>
                ) : caseDetails?.nextSteps ? (
                  <Card>
                    <CardContent className="p-4">
                      <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 bg-slate-50 p-4 rounded-lg">
                        {caseDetails.nextSteps.next_steps}
                      </pre>
                      <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-400">
                        <p className="italic">{caseDetails.nextSteps.disclaimer}</p>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="py-8 text-center">
                    <Target className="h-12 w-12 mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-500">Click to load next step predictions</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="insights" className="mt-4">
                {loadingDetails ? (
                  <div className="py-8 text-center">
                    <RefreshCw className="h-6 w-6 mx-auto animate-spin text-purple-600" />
                  </div>
                ) : caseDetails?.insights ? (
                  <Card>
                    <CardContent className="p-4">
                      <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 bg-slate-50 p-4 rounded-lg">
                        {caseDetails.insights.insights}
                      </pre>
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <p className="text-xs text-slate-400">
                          Based on {caseDetails.insights.data_sources?.similar_cases_analyzed || 0} similar cases
                        </p>
                        <p className="text-xs text-slate-400 italic mt-1">{caseDetails.insights.disclaimer}</p>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="py-8 text-center">
                    <Zap className="h-12 w-12 mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-500">Click to load pattern insights</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setSelectedCase(null)}>Close</Button>
            <Button onClick={() => navigate(`/cases`)} className="gap-2">
              <Eye className="h-4 w-4" />
              View Full Case
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CaseHealthDashboard;
